// Live commerce-service client (gateway → /api/v1/commerce/*). Same auth pattern as
// the credit client: the httpOnly session cookie (attachCookieAuth) and never nuke
// the session on error. Errors propagate so callers can surface the backend message
// via getApiErrorMessage.
//
// ⚠️ The LUKU token returned by getToken() is a bearer value worth money: callers
// keep it in component state only — never log it, persist it, or put it in a URL.
import axios from 'axios';
import { apiConfig } from '@/config/api.config';
import { attachCookieAuth } from '@/lib/session';
import type {
  Availability,
  Catalogue,
  CommerceSettings,
  CompanyRequest,
  CompanyRequestInput,
  CreateOrderInput,
  Offer,
  OperatorOption,
  Order,
  OrderToken,
  ProductOption,
  ProductType,
  SavedPayer,
  SavedRecipient,
} from '../types';

const BASE = '/api/v1/commerce';

/** Exported for tests (adapter injection); app code goes through commerceApi. */
export const commerceHttp = axios.create({ baseURL: apiConfig.baseURL, timeout: 20000, withCredentials: true });
commerceHttp.interceptors.request.use(attachCookieAuth);

const num = (v: unknown): number => (v == null || v === '' ? 0 : Number(v));
const optNum = (v: unknown): number | undefined => (v == null || v === '' ? undefined : Number(v));

// --- mappers (snake_case API → camelCase UI) ---

function mapProduct(p: any): ProductOption {
  return {
    productType: p.product_type as ProductType,
    currency: p.currency || 'TZS',
    minAmount: optNum(p.min_amount_minor),
    maxAmount: optNum(p.max_amount_minor),
  };
}

function mapOperator(o: any): OperatorOption {
  return { code: String(o.code ?? ''), name: String(o.name ?? o.code ?? '') };
}

export function mapAvailability(a: any): Availability {
  return {
    enabled: a?.enabled === true,
    products: Array.isArray(a?.products) ? a.products.map(mapProduct) : [],
    operators: Array.isArray(a?.operators) ? a.operators.map(mapOperator) : [],
  };
}

export function mapOrder(o: any): Order {
  return {
    id: String(o.id),
    status: o.status ?? '',
    paymentStatus: o.payment_status ?? '',
    fulfillmentStatus: o.fulfillment_status ?? '',
    productType: o.product_type ?? '',
    operator: o.operator ?? '',
    recipient: o.recipient ?? '',
    currency: o.currency || 'TZS',
    faceValue: num(o.face_value_minor),
    fee: num(o.fee_minor),
    customerTotal: num(o.customer_total_minor),
    createdAt: o.created_at ?? '',
    updatedAt: o.updated_at ?? '',
    purchaseContext: o.purchase_context ?? 'personal',
    recipientRelation: o.recipient_relation ?? 'self',
    recipientLabel: o.recipient_label ?? '',
    payer: o.payer ?? '',
    productName: o.product_name ?? '',
    validity: o.validity ?? '',
  };
}

function mapOffer(o: any, currency: string): Offer {
  return {
    id: String(o.id),
    name: String(o.name ?? ''),
    price: optNum(o.price_minor),
    currency: o.currency || currency,
    validity: o.validity ?? '',
    volume: o.volume ?? '',
  };
}

export function mapCatalogue(c: any): Catalogue {
  const currency = c?.currency || 'TZS';
  return {
    productType: c?.product_type ?? '',
    operator: c?.operator ?? '',
    currency,
    minAmount: optNum(c?.min_amount_minor),
    maxAmount: optNum(c?.max_amount_minor),
    offers: Array.isArray(c?.offers) ? c.offers.map((o: any) => mapOffer(o, currency)) : [],
  };
}

function mapRecipient(r: any): SavedRecipient {
  return { id: String(r.id), label: r.label ?? '', recipient: r.recipient ?? '', operator: r.operator ?? '' };
}

function mapPayer(p: any): SavedPayer {
  return {
    methodId: String(p.method_id),
    masked: p.masked ?? '',
    provider: p.provider ?? '',
    eligible: p.eligible === true,
    reason: p.reason || undefined,
  };
}

export function mapCompanyRequest(r: any): CompanyRequest {
  return {
    id: String(r.id),
    entityId: String(r.entity_id ?? ''),
    requesterId: String(r.requester_id ?? ''),
    purpose: r.purpose ?? '',
    costCentre: r.cost_centre ?? '',
    currency: r.currency || 'TZS',
    total: num(r.total_minor),
    status: r.status ?? '',
    approverId: r.approver_id ?? undefined,
    decidedAt: r.decided_at ?? undefined,
    decisionNote: r.decision_note ?? '',
    approvalValid: r.approval_valid === true,
    executionBlockedReason: r.execution_blocked_reason ?? '',
    lines: Array.isArray(r.lines) ? r.lines.map((l: any) => ({
      id: String(l.id),
      recipientKind: l.recipient_kind ?? '',
      employeeId: l.employee_id ?? undefined,
      recipientLabel: l.recipient_label ?? '',
      recipient: l.recipient ?? '',
      operator: l.operator ?? '',
      productType: l.product_type ?? '',
      amount: num(l.amount_minor),
      status: l.status ?? '',
      orderId: l.order_id ?? undefined,
    })) : [],
    summary: {
      lines: num(r.summary?.lines), delivered: num(r.summary?.delivered), pending: num(r.summary?.pending),
      failed: num(r.summary?.failed), unknown: num(r.summary?.unknown),
    },
    createdAt: r.created_at ?? '',
    updatedAt: r.updated_at ?? '',
  };
}

function companyBody(input: CompanyRequestInput): Record<string, unknown> {
  return {
    entity_id: input.entityId,
    purpose: input.purpose,
    cost_centre: input.costCentre,
    currency: input.currency,
    lines: input.lines.map((l) => ({
      recipient_kind: l.recipientKind,
      employee_id: l.employeeId || undefined,
      recipient_label: l.recipientLabel || '',
      recipient: l.recipient,
      operator: l.operator,
      product_type: l.productType,
      provider_product_id: l.offerId || undefined,
      amount_minor: Math.round(l.amount),
    })),
  };
}

function mapSettings(s: any): CommerceSettings {
  return { enabled: s?.enabled === true, updatedAt: s?.updated_at ?? undefined };
}

export const commerceApi = {
  async availability(country = 'TZ'): Promise<Availability> {
    const res = await commerceHttp.get(`${BASE}/availability`, { params: { country } });
    return mapAvailability(res.data);
  },

  /**
   * Place an order. `idempotencyKey` must be generated ONCE per purchase attempt and
   * reused verbatim on any retry of that same attempt (see lib/purchaseAttempt), so a
   * timeout-then-retry can never buy twice: the server returns the original order.
   */
  async createOrder(input: CreateOrderInput, idempotencyKey: string): Promise<Order> {
    const body: Record<string, unknown> = {
      country_code: input.countryCode,
      product_type: input.productType,
      recipient: input.recipient,
      payer_msisdn: input.payerMsisdn,
      payer_confirmed: input.payerConfirmed === true,
      amount_minor: Math.round(input.amount),
      currency: input.currency,
    };
    if (input.productType !== 'ELECTRICITY' && input.operator) body.operator = input.operator;
    if (input.savedRecipientId) body.saved_recipient_id = input.savedRecipientId;
    if (input.recipientRelation) body.recipient_relation = input.recipientRelation;
    if (input.recipientLabel) body.recipient_label = input.recipientLabel;
    if (input.saveRecipient) body.save_recipient = true;
    if (input.payerMethodId) body.payer_method_id = input.payerMethodId;
    if (input.offerId) body.provider_product_id = input.offerId;
    const res = await commerceHttp.post(`${BASE}/orders`, body, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return mapOrder(res.data);
  },

  async listOrders(limit = 50): Promise<Order[]> {
    const res = await commerceHttp.get(`${BASE}/orders`, { params: { limit } });
    const items = Array.isArray(res.data?.items) ? res.data.items : [];
    return items.map(mapOrder);
  },

  async getOrder(id: string): Promise<Order> {
    const res = await commerceHttp.get(`${BASE}/orders/${encodeURIComponent(id)}`);
    return mapOrder(res.data);
  },

  /** The LUKU token for a delivered electricity order; `null` while not issued yet (404). */
  async getToken(id: string): Promise<OrderToken | null> {
    try {
      const res = await commerceHttp.get(`${BASE}/orders/${encodeURIComponent(id)}/token`);
      return { token: String(res.data?.token ?? ''), units: String(res.data?.units ?? '') };
    } catch (e: any) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },

  /** Download the PDF receipt (COMPLETED or REFUNDED only; 409 otherwise). */
  async downloadReceipt(id: string): Promise<void> {
    let res;
    try {
      res = await commerceHttp.get(`${BASE}/orders/${encodeURIComponent(id)}/receipt`, { responseType: 'blob' });
    } catch (e: any) {
      // A blob request gets its JSON error body as a Blob too — decode it so
      // getApiErrorMessage can surface the backend's message (e.g. the 409).
      const data = e?.response?.data;
      if (data && typeof data.text === 'function') {
        try { e.response.data = JSON.parse(await data.text()); } catch { /* leave as-is */ }
      }
      throw e;
    }
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** Options for one product + network, priced by the server. */
  async catalogue(productType: ProductType, operator: string, country = 'TZ'): Promise<Catalogue> {
    const res = await commerceHttp.get(`${BASE}/catalogue`, { params: { country, product_type: productType, operator } });
    return mapCatalogue(res.data);
  },

  /** The buyer's own saved mobile-money numbers, masked, with collection eligibility. */
  async payerSuggestion(productType: ProductType, operator: string, country = 'TZ'): Promise<SavedPayer[]> {
    const res = await commerceHttp.get(`${BASE}/payer-suggestion`, { params: { country, product_type: productType, operator } });
    return Array.isArray(res.data?.items) ? res.data.items.map(mapPayer) : [];
  },

  async listRecipients(): Promise<SavedRecipient[]> {
    const res = await commerceHttp.get(`${BASE}/recipients`);
    return Array.isArray(res.data?.items) ? res.data.items.map(mapRecipient) : [];
  },

  async deleteRecipient(id: string): Promise<void> {
    await commerceHttp.delete(`${BASE}/recipients/${encodeURIComponent(id)}`);
  },

  // --- company purchases (commerce:company:purchase / :approve) ---

  async listCompanyRequests(): Promise<CompanyRequest[]> {
    const res = await commerceHttp.get(`${BASE}/company/requests`);
    return Array.isArray(res.data?.items) ? res.data.items.map(mapCompanyRequest) : [];
  },

  async createCompanyRequest(input: CompanyRequestInput): Promise<CompanyRequest> {
    const res = await commerceHttp.post(`${BASE}/company/requests`, companyBody(input));
    return mapCompanyRequest(res.data);
  },

  async updateCompanyRequest(id: string, input: CompanyRequestInput): Promise<CompanyRequest> {
    const res = await commerceHttp.put(`${BASE}/company/requests/${encodeURIComponent(id)}`, companyBody(input));
    return mapCompanyRequest(res.data);
  },

  async companyAction(id: string, action: 'submit' | 'approve' | 'reject' | 'cancel' | 'execute', note?: string): Promise<CompanyRequest> {
    const res = await commerceHttp.post(`${BASE}/company/requests/${encodeURIComponent(id)}/${action}`, note ? { note } : {});
    return mapCompanyRequest(res.data);
  },

  /** Employer switch (permission commerce:settings — tenant owner). */
  async getSettings(): Promise<CommerceSettings> {
    const res = await commerceHttp.get(`${BASE}/settings`);
    return mapSettings(res.data);
  },

  async updateSettings(enabled: boolean): Promise<CommerceSettings> {
    const res = await commerceHttp.put(`${BASE}/settings`, { enabled });
    return mapSettings(res.data);
  },
};

export default commerceApi;
